import { FunctionComponent, useContext, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import contextViewport from '@contexts/viewport'
import contextToiTeaser from '@contexts/toiTeaser'
import { LogoProps } from '@widgets/Teaser/toi/types'
import { mobileCSS } from '@measures/responsive'
import FreeRatioImage from '@components/FreeRatioImage'
import { calculateSingleConstraintDimensions } from '@components/FreeRatioImage/utils'

interface StyledLogoProps {
  mobileWidth: number
  mobileHeight: number
}

const { ViewportTypeContext, ViewportWidthContext } = contextViewport
const { TOITeaserLayoutContext } = contextToiTeaser

const StyledLogo = styled.div<StyledLogoProps>`
  ${({ mobileWidth, mobileHeight }) => {
    return css`
      width: auto;
      height: auto;

      ${mobileCSS(css`
        width: ${mobileWidth}px;
        height: ${mobileHeight}px;
        > * {
          width: 100%;
          height: 100%;
        }
        picture > img {
          width: 100%;
          height: 100%;
        }
      `)}
    `
  }}
`

const Logo: FunctionComponent<LogoProps> = ({
  src,
  alt,
  width,
  height,
  crops,
}) => {
  const freeCrop = crops?.['free']
  const viewportType = useContext(ViewportTypeContext)
  const viewportWidth = useContext(ViewportWidthContext)
  const { teaserFlex } = useContext(TOITeaserLayoutContext)

  let maxWidthDesktop = 0
  let maxHeightDesktop = 0
  const desktopTeaserFlex = teaserFlex['desktop']
  if (desktopTeaserFlex === '6' || desktopTeaserFlex === '4') {
    maxWidthDesktop = 120
    maxHeightDesktop = 100
  } else if (desktopTeaserFlex === '3') {
    maxWidthDesktop = 98
    maxHeightDesktop = 82
  } else if (desktopTeaserFlex === '2') {
    maxWidthDesktop = 77
    maxHeightDesktop = 64
  }

  let maxWidthTablet = 0
  let maxHeightTablet = 0
  const tabletTeaserFlex = teaserFlex['tablet']
  if (tabletTeaserFlex === '6') {
    maxWidthTablet = 120
    maxHeightTablet = 100
  } else if (tabletTeaserFlex === '3') {
    maxWidthTablet = 77
    maxHeightTablet = 64
  }

  const maxWidthMobile = 90
  const maxHeightMobile = 75

  const [actualMobileWidth, setActualMobileWidth] =
    useState<number>(maxWidthMobile)
  const [actualMobileHeight, setActualMobileHeight] =
    useState<number>(maxHeightMobile)

  useEffect(() => {
    if (viewportType === 'mobile') {
      const { width: calculatedMobileWidth, height: calculatedMobileHeight } =
        calculateSingleConstraintDimensions({
          constraint: {
            width: Math.floor((viewportWidth * 24) / 100),
            height: Math.floor((viewportWidth * 20) / 100),
          },
          originalDimensions: freeCrop
            ? { width: freeCrop.width, height: freeCrop.height }
            : { width, height },
        })
      setActualMobileWidth(calculatedMobileWidth)
      setActualMobileHeight(calculatedMobileHeight)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewportType, viewportWidth])

  return (
    <StyledLogo
      mobileWidth={actualMobileWidth}
      mobileHeight={actualMobileHeight}>
      <FreeRatioImage
        src={src}
        alt={alt ?? ''}
        originalDimensions={{ width, height }}
        constraints={{
          desktop: { width: maxWidthDesktop, height: maxHeightDesktop },
          tablet: { width: maxWidthTablet, height: maxHeightTablet },
          mobile: { width: maxWidthMobile, height: maxHeightMobile },
          mobileSmall: { width: maxWidthMobile, height: maxHeightMobile },
        }}
        crops={crops ?? {}}
      />
    </StyledLogo>
  )
}

export default Logo
